import * as v from 'valibot'

/**
 * OpenApply's year groups, in order. The labels are OpenApply's own (its `grade`), so they go
 * across exactly as written — `grade` refuses anything that isn't one of the school's grades.
 */
export const year_groups = [
	'Pre-Nursery',
	'Nursery',
	'Reception',
	'Year 1',
	'Year 2',
	'Year 3',
	'Group 1 (Year 4)',
	'Group 2 (Year 5)',
	'Group 3 (Year 6)',
	'Block 1 (Year 7)',
	'Block 2 (Year 8)',
	'Block 3 (Year 9)',
	'Block 4 (Year 10)',
	'Block 5 (Year 11)',
	'6.1 (Year 12, Lower Sixth Form)',
	'6.2 (Year 13, Upper Sixth Form)'
] as const

/**
 * OpenApply's "Where did you hear about Bedales?" (`tour_get_in_touch_reason`): each option's label,
 * shown on the form, and its id, which is what OpenApply takes for a select field.
 */
const hear = {
	'Advert ': 482511,
	'Social media advert (Facebook, Instagram etc)': 482518,
	'Press/media coverage': 482519,
	Agent: 482512,
	'Current school': 482514,
	'School fair': 482520,
	'General reputation': 482516,
	'Good Schools Guide': 482517,
	'Educational directories': 482515,
	'I work at Bedales': 518037,
	'I am an Old Bedalian': 489065,
	'Alumni connection': 518038,
	'Staff connection': 489066,
	'Recommendation from a current parent': 489064,
	'Word of mouth recommendation': 482521,
	'Independent Schools Show - Battersea': 518039,
	'Independent Schools Show - Hurlingham': 518040,
	'Independent Schools Show - Hong Kong': 518041,
	'Independent Schools Show - Singapore': 518042
} as const

export const hear_options = Object.keys(hear) as Array<keyof typeof hear>

/** Day or boarding, as the form asks it, to its option in OpenApply's `day_boarding_custom`. */
export const boarding_options = {
	day: 482553, // Day
	boarding: 482552, // Boarder
	unsure: 482556 // Not Yet Decided
} as const

/** Day or boarding, as the form and the email word it. */
export const boarding_labels = { day: 'Day', boarding: 'Boarding', unsure: 'Not sure' } as const

/** "Other", in OpenApply's `name_of_current_school`: the school's name then goes in the text field. */
const other_school = 487494

/** The most children one request can be for. */
export const max_children = 3

const text = v.pipe(v.string(), v.trim())
const required = v.pipe(text, v.nonEmpty())
const optional = v.optional(text, '')

export const Request = v.object({
	children: v.pipe(
		v.array(
			v.object({
				first_name: required,
				current: v.picklist(year_groups),
				entry: v.picklist(year_groups),
				school: optional
			})
		),
		v.minLength(1),
		v.maxLength(max_children)
	),
	boarding: v.optional(v.picklist(Object.keys(boarding_options) as Array<keyof typeof boarding_options>)),
	location: v.picklist(['uk', 'international']),
	first_name: required,
	last_name: required,
	phone: required,
	email: v.pipe(required, v.email()),
	hear: v.picklist(hear_options),
	consent: v.boolean()
})

export type Request = v.InferOutput<typeof Request>

/** The form's fields as the shape a {@link Request} takes, unchecked: what's been filled in so far. */
export function collect(data: FormData) {
	const all = (name: string) => data.getAll(name).map(String)
	const entries = all('child_entry')
	const text = (name: string) => {
		const value = data.get(name)
		return typeof value === 'string' ? value : ''
	}

	return {
		// each child posts all four fields, blank or not, so the arrays line up
		children: all('child_first_name').map((first_name, i) => ({
			first_name,
			current: all('child_current')[i] ?? '',
			entry: entries[i] || undefined,
			school: all('child_school')[i] ?? ''
		})),
		boarding: text('boarding') || undefined,
		location: text('location') || undefined,
		first_name: text('first_name'),
		last_name: text('last_name'),
		phone: text('phone'),
		email: text('email'),
		hear: text('hear'),
		consent: data.get('consent') === 'yes'
	}
}

export type Answers = ReturnType<typeof collect>

/** The form's post as a {@link Request}, or the issues that stop it being one. */
export function parse(data: FormData) {
	return v.safeParse(Request, collect(data))
}

/**
 * The academic year a child would start in their entry year group, as OpenApply counts it: the
 * calendar year the school year begins (2026 is 2026–27, and OpenApply's years turn over on 30
 * August). Undefined without an entry year group, or one behind where the child is now.
 */
export function entry_year(current: string, entry: string | undefined, today = new Date()) {
	if (!entry) return undefined
	const gap = year_groups.indexOf(entry as never) - year_groups.indexOf(current as never)
	if (gap < 0) return undefined
	const turnover = new Date(today.getFullYear(), 7, 30)
	return (today >= turnover ? today.getFullYear() : today.getFullYear() - 1) + gap
}

/**
 * The OpenApply custom fields the request fills in. The API keys custom fields by these ids, not
 * their slugs (the slugs are alongside), and a select field takes its option's id, not its label;
 * they're all Bedales' own, from `GET /api/v3/fields`.
 */
export const fields = {
	hear: 355476, // tour_get_in_touch_reason
	boarding: 355535, // day_boarding_custom
	notes: 355558, // roi_enquiry_notes
	school: 360201, // name_of_current_school
	school_other: 361393, // current_school_other
	mobile: 75 // parent mobile_phone
}

/** The custom fields that take an option's id, which OpenApply refuses once the option has gone. */
const selects = [fields.hear, fields.boarding, fields.school]

/**
 * A student's custom fields without the select fields, their answers written into the notes:
 * for when OpenApply refuses the fields as they are (an option renamed or removed there), so
 * the student still lands complete apart from those. The school's name is in its own text field
 * already, so only the other two need noting.
 */
export function without_selects(custom_fields: Record<number, unknown>, request: Request) {
	const answers = [
		fields.hear in custom_fields && `Heard about Bedales: ${request.hear.trim()}`,
		fields.boarding in custom_fields &&
			request.boarding &&
			`Day or boarding: ${boarding_labels[request.boarding]}`
	].filter(Boolean)
	const kept = Object.fromEntries(Object.entries(custom_fields).filter(([id]) => !selects.includes(Number(id))))
	kept[fields.notes] = [
		custom_fields[fields.notes],
		answers.length && `Not accepted by OpenApply's own fields, so noted here:\n${answers.join('\n')}`
	]
		.filter(Boolean)
		.join('\n')
	return kept
}

/** Reading a student back, its custom fields come keyed by slug instead. */
export const slugs = {
	hear: 'tour_get_in_touch_reason',
	boarding: 'day_boarding_custom',
	notes: 'roi_enquiry_notes',
	school: 'name_of_current_school',
	school_other: 'current_school_other'
} as const

/** A student the parent already has in OpenApply, as much of it as a repeat request looks at. */
export type Existing = {
	id: number
	first_name?: string | null
	grade?: string | null
	enrollment_year?: number | null
	country?: string | null
	custom_fields?: Partial<Record<(typeof slugs)[keyof typeof slugs], string | null>>
}

/** The parent's existing student this child is, by first name, if any. */
export function match(existing: Array<Existing>, child: Request['children'][number]) {
	const name = (value?: string | null) => value?.trim().toLocaleLowerCase()
	return existing.find(student => name(student.first_name) === name(child.first_name))
}

/** One child's OpenApply student record and custom fields, from the request. */
export function student(request: Request, child: Request['children'][number], today = new Date()) {
	const notes = [
		'Prospectus request from the website.',
		`Current year group: ${child.current}`,
		request.location && `Based: ${request.location === 'uk' ? 'UK' : 'International'}`,
		`Marketing consent: ${request.consent ? 'Yes' : 'No'}`
	]

	return {
		record: {
			first_name: child.first_name,
			// ponytail: the form doesn't ask for the child's surname and OpenApply needs one, so the
			// parent's stands in. Add a field to the form if admissions find it wrong too often.
			last_name: request.last_name,
			status: 'pending', // "Enquired" at Bedales
			grade: child.entry,
			enrollment_year: entry_year(child.current, child.entry, today)
		},
		// OpenApply only takes a country as its two-letter code, and only through an update
		country: request.location === 'uk' ? 'GB' : undefined,
		custom_fields: {
			[fields.hear]: hear[request.hear],
			...(request.boarding && { [fields.boarding]: boarding_options[request.boarding] }),
			...(child.school && { [fields.school]: other_school, [fields.school_other]: child.school }),
			[fields.notes]: notes.filter(Boolean).join('\n')
		}
	}
}

/**
 * A repeat request for a child OpenApply already has: only what's blank there gets filled, the
 * status is left alone (they may be well past "Enquired"), and the request's notes are added under
 * whatever notes there already are, dated.
 */
export function update(
	existing: Existing,
	request: Request,
	child: Request['children'][number],
	today = new Date()
) {
	const fresh = student(request, child, today)
	const blank = (value: unknown) => value === null || value === undefined || value === ''

	const record = {
		...(blank(existing.grade) && fresh.record.grade && { grade: fresh.record.grade }),
		...(blank(existing.enrollment_year) &&
			fresh.record.enrollment_year && { enrollment_year: fresh.record.enrollment_year }),
		...(blank(existing.country) && fresh.country && { country: fresh.country })
	}

	const custom_fields: Record<number, unknown> = {}
	for (const key of ['hear', 'boarding', 'school', 'school_other'] as const) {
		const value = fresh.custom_fields[fields[key]]
		if (value !== undefined && blank(existing.custom_fields?.[slugs[key]])) custom_fields[fields[key]] = value
	}
	const before = existing.custom_fields?.[slugs.notes]
	const dated = `${today.toISOString().slice(0, 10)}: ${fresh.custom_fields[fields.notes]}`
	custom_fields[fields.notes] = before ? `${before}\n\n${dated}` : dated

	return { record, custom_fields }
}

/** "Ada", "Ada & Bo", "Ada, Bo & Cy": the design's way of naming the children together. */
export function together(names: Array<string>) {
	return names.length > 1 ? `${names.slice(0, -1).join(', ')} & ${names.at(-1)}` : (names[0] ?? '')
}

/**
 * Which parts of the school the children would join, by their entry year group (or the one
 * they're in now, if they didn't say): the Senior School takes Block 3 (Year 9) up, and Prep and
 * Pre-prep everything younger. Admissions has a phone line for each.
 */
export function schools(children: Request['children']) {
	const senior = year_groups.indexOf('Block 3 (Year 9)')
	const found = new Set(
		children.map(({ entry, current }) =>
			year_groups.indexOf((entry || current) as never) >= senior ? 'senior' : 'prep'
		)
	)
	return (['senior', 'prep'] as const).filter(school => found.has(school))
}
